import { useRef, useState } from 'react';
import {
  useOpenTemplateDraft,
  type StoreWebsiteEditorStateV3,
  type SupabaseClient,
  type WebsiteTemplateCatalogItemV3,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useWebsiteT } from '../hooks/use-website-t';
import { stageErrorKey } from '../lib/issue-messages';

export interface UseTemplateDialogProps {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly item: WebsiteTemplateCatalogItemV3;
  readonly editorState: StoreWebsiteEditorStateV3;
  /** The template's draft exists. The merchant goes to its edit route. */
  readonly onDone: (templateId: string) => void;
  /** Cancelled, or closed: nothing was created. */
  readonly onClose: () => void;
  readonly newMutationId?: () => string;
}

/**
 * "Use <template>?" (first use, epic #228 P3). A click on "Use template"
 * never changes anything by itself: this question comes first, with Cancel.
 * Confirming creates this template's own draft with its default content;
 * no other template's draft changes and nothing is published until the
 * merchant clicks Publish.
 */
export function UseTemplateDialog({
  client,
  storeId,
  item,
  editorState,
  onDone,
  onClose,
  newMutationId = () => crypto.randomUUID(),
}: UseTemplateDialogProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const open = useOpenTemplateDraft(client, storeId);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  // A retry of the same confirm reuses its id: the server replays the first result.
  const mutationIdRef = useRef<string | null>(null);
  // The library mounts this dialog without a Radix Trigger: remember the opener
  // so Cancel and Escape give focus back to it (as the demo dialog does).
  const openerRef = useRef<HTMLElement | null>(null);
  const dismissedRef = useRef(false);
  const dismiss = (): void => {
    dismissedRef.current = true;
    onClose();
  };

  const confirm = async (): Promise<void> => {
    mutationIdRef.current ??= newMutationId();
    setErrorKey(null);
    try {
      // The shared hook refreshes this template's editor state before it settles.
      await open.mutateAsync({
        templateId: item.templateId,
        clientMutationId: mutationIdRef.current,
      });
      mutationIdRef.current = null;
      onDone(item.templateId);
    } catch (error) {
      setErrorKey(stageErrorKey(error));
    }
  };

  const convertedNote = editorState.conversion === 'converted' && item.kind !== 'legacy-frozen';

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !open.isPending) dismiss();
      }}
    >
      <DialogContent
        data-testid="use-template-dialog"
        data-stage="confirm"
        onOpenAutoFocus={() => {
          const target = document.activeElement;
          openerRef.current = target instanceof HTMLElement ? target : null;
        }}
        onCloseAutoFocus={(event) => {
          // Only a dismissal returns focus; a confirmed open navigates away.
          event.preventDefault();
          const opener = openerRef.current;
          if (!dismissedRef.current || !opener?.isConnected) return;
          if (opener.matches(':disabled, [aria-disabled="true"]') || opener.closest('[inert]'))
            return;
          opener.focus({ preventScroll: true });
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t('admin.websiteTemplates.stage.title', { name: t(item.titleKey) })}
          </DialogTitle>
          <DialogDescription>{t('admin.websiteTemplates.useTemplate.noPublish')}</DialogDescription>
        </DialogHeader>

        {convertedNote ? (
          <p className="text-muted-foreground text-sm">
            {t('admin.websiteTemplates.stage.originalDesignNote')}
          </p>
        ) : null}

        {errorKey === null ? null : (
          <p role="alert" data-testid="use-template-error" className="text-destructive text-sm">
            {t(errorKey)}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={open.isPending}
            data-testid="use-template-cancel"
            onClick={dismiss}
          >
            {t('admin.websiteTemplates.common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={open.isPending}
            data-testid="use-template-confirm"
            onClick={() => void confirm()}
          >
            {open.isPending
              ? t('admin.websiteTemplates.stage.applying')
              : t('admin.websiteTemplates.stage.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
